<script setup lang="ts">
import { computed, ref, watch } from 'vue';
// biome-ignore lint/correctness/noUnusedImports: used via <ClassicPortraitGalleryTile> in <template>
import ClassicPortraitGalleryTile from './ClassicPortraitGalleryTile.vue';
import {
  balancedRowCapacity,
  type ClassicPortraitGalleryOrientation,
  type ClassicPortraitGallerySectionOrder,
  type GalleryEntry,
  galleryTileBindings,
  type MixedPortraitGalleryItem,
  type MixedPortraitGalleryKind,
  mixedItemRatio,
  type PackedRow,
  packGalleryRows,
  packMixedGalleryRows,
} from './classic-mixed-portrait-gallery';

interface Props {
  activeUuid?: string | null;
  // Vertical portraits per row (fractions allowed); sets the row height.
  columns: number;
  // Grow the capacity up to this many units when that puts every item on
  // one page.
  maxColumns?: number;
  // A row not full to `columns` grows to fill the width, up to this much.
  maxRowFill?: number;
  // Units per row for one kind, when sections are on.
  sectionColumns?: Partial<Record<MixedPortraitGalleryKind, number>>;
  // Whether each page opens with a group row, or every group row comes
  // first and the singles follow.
  sectionOrder?: ClassicPortraitGallerySectionOrder;
  controls?: 'bottom' | 'side' | 'sides';
  entries: GalleryEntry[];
  gap: string;
  generateMoreDisabled?: boolean;
  getSelectedPortraitCount: (
    item: MixedPortraitGalleryItem
  ) => number | undefined;
  orientation?: ClassicPortraitGalleryOrientation;
  portraitRatio: number;
  // Rows per page.
  rows: number;
  // Pack group and individual tiles into rows of their own, with a line
  // where the group rows end and a caption over each section.
  sections?: boolean;
  selectedUuids: string[];
  selectionOnTile?: boolean;
  showGenerateMore?: boolean;
}

interface GalleryRow {
  entries: GalleryEntry[];
  // How much the row may grow to fill the frame's width (1 = it is full).
  fill: number;
  kind: MixedPortraitGalleryKind | 'mixed';
  sectionStart: boolean;
}

const SECTION_LABEL_KEYS: Record<MixedPortraitGalleryKind, string> = {
  group: 'Portraits.filterGroup',
  individual: 'Portraits.filterIndividual',
};

const props = withDefaults(defineProps<Props>(), {
  activeUuid: null,
  controls: 'side',
  generateMoreDisabled: false,
  maxRowFill: 2,
  orientation: 'landscape',
  sections: false,
  sectionOrder: 'interleave',
  selectionOnTile: false,
  showGenerateMore: false,
});
const emit = defineEmits<{
  activate: [item: MixedPortraitGalleryItem, index: number, event: MouseEvent];
  countDecrement: [event: MouseEvent, item: MixedPortraitGalleryItem];
  countIncrement: [event: MouseEvent, item: MixedPortraitGalleryItem];
  generateMore: [];
  itemLoad: [item: MixedPortraitGalleryItem, index: number, event: Event];
  tryAgain: [item: MixedPortraitGalleryItem];
}>();
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const { tileBindings, tileKey } = galleryTileBindings(props, emit);

const page = ref(0);

function unitsOf(entry: GalleryEntry): number {
  return mixedItemRatio(entry.item) / props.portraitRatio;
}

const leadsRow = (entry: GalleryEntry): boolean => entry.item.kind === 'group';

function packEntries(
  entries: GalleryEntry[],
  capacity: number
): PackedRow<GalleryEntry>[] {
  return props.sections
    ? packGalleryRows(entries, unitsOf, capacity)
    : packMixedGalleryRows(entries, unitsOf, capacity, leadsRow);
}

const capacity = computed(() => {
  if (!props.maxColumns) {
    return props.columns;
  }
  const units = props.entries.reduce((sum, entry) => sum + unitsOf(entry), 0);
  return balancedRowCapacity(
    units,
    props.columns,
    props.maxColumns,
    (candidate) => packEntries(props.entries, candidate).length
  );
});

// A tile costs its width in vertical-portrait units and a row holds
// `columns` of them (or a kind's own share). A row that uses fewer grows to
// fill the width: its fill is the rail's capacity over the units it holds.
function packKind(
  kind: MixedPortraitGalleryKind | null
): Omit<GalleryRow, 'sectionStart'>[] {
  const rowCapacity = (kind && props.sectionColumns?.[kind]) || capacity.value;
  return packEntries(
    kind
      ? props.entries.filter(({ item }) => item.kind === kind)
      : props.entries,
    rowCapacity
  ).map((row) => ({
    entries: row.items,
    fill: Math.min(
      props.maxRowFill,
      Math.max(1, capacity.value / (rowCapacity - row.free))
    ),
    kind: rowKind(row.items),
  }));
}

// With sections, every page opens with a row of groups and the rows of
// singles follow, so a third group portrait does not push every single one
// off the first page. Group rows left over once the singles are out fill
// the pages that remain.
function interleaveSections(
  groupRows: Omit<GalleryRow, 'sectionStart'>[],
  singleRows: Omit<GalleryRow, 'sectionStart'>[],
  perPage: number
): Omit<GalleryRow, 'sectionStart'>[] {
  const ordered: Omit<GalleryRow, 'sectionStart'>[] = [];
  let group = 0;
  let single = 0;
  while (group < groupRows.length || single < singleRows.length) {
    let slots = perPage;
    if (group < groupRows.length) {
      ordered.push(groupRows[group++] as Omit<GalleryRow, 'sectionStart'>);
      slots -= 1;
    }
    while (slots > 0 && single < singleRows.length) {
      ordered.push(singleRows[single++] as Omit<GalleryRow, 'sectionStart'>);
      slots -= 1;
    }
    while (slots > 0 && group < groupRows.length) {
      ordered.push(groupRows[group++] as Omit<GalleryRow, 'sectionStart'>);
      slots -= 1;
    }
  }
  return ordered;
}

const rows = computed<GalleryRow[]>(() => {
  if (!props.sections) {
    return packKind(null).map((row, index) => ({
      ...row,
      sectionStart: index === 0,
    }));
  }
  const groupRows = packKind('group');
  const singleRows = packKind('individual');
  const ordered =
    props.sectionOrder === 'interleave'
      ? interleaveSections(groupRows, singleRows, props.rows)
      : [...groupRows, ...singleRows];
  const perPage = props.rows;
  // A section starts where a page starts or the kind changes.
  return ordered.map((row, index) => ({
    ...row,
    sectionStart:
      index % perPage === 0 || ordered[index - 1]?.kind !== row.kind,
  }));
});

function rowKind(entries: GalleryEntry[]): GalleryRow['kind'] {
  const kind = entries[0]?.item.kind;
  return kind && entries.every(({ item }) => item.kind === kind)
    ? kind
    : 'mixed';
}

// Rows past `rows` are paged with the controls, never scrolled. On the last
// page the forward arrow gives way to "+", as in the old carousel.
const rowsPerPage = computed(() => props.rows);
const pageCount = computed(() =>
  Math.max(1, Math.ceil(rows.value.length / rowsPerPage.value))
);
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const onLastPage = computed(() => page.value >= pageCount.value - 1);
watch(pageCount, (count) => {
  page.value = Math.min(page.value, count - 1);
});

// Portrait always keeps the controls beside the rows.
const controlsPlacement = computed(() =>
  props.orientation === 'portrait' || props.controls === 'sides'
    ? 'beside'
    : props.controls
);
// The frame's height is shared out among the page's rows by their fill.
const pageFillSum = computed(() => {
  const start = page.value * rowsPerPage.value;
  return rows.value
    .slice(start, start + rowsPerPage.value)
    .reduce((sum, row) => sum + row.fill, 0);
});
const sectionsOnPage = computed(() => {
  const start = page.value * rowsPerPage.value;
  return rows.value
    .slice(start, start + rowsPerPage.value)
    .filter((row, offset) => row.sectionStart && offset > 0).length;
});

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const rootClasses = computed(() => [
  `ClassicMixedPortraitGallery__rows--${props.orientation}`,
  `ClassicMixedPortraitGallery__rows--controls-${controlsPlacement.value}`,
  rowsPerPage.value === 1 && 'ClassicMixedPortraitGallery__rows--single-row',
  props.sections && 'ClassicMixedPortraitGallery__rows--divided',
  pageCount.value <= 1 && 'ClassicMixedPortraitGallery__rows--single-page',
]);
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const rootStyle = computed(() => ({
  '--cmg-rail-columns': String(capacity.value),
  '--cmg-rail-fill-sum': String(pageFillSum.value || 1),
  '--cmg-rail-gap': props.gap,
  '--cmg-rail-portrait-ratio': String(props.portraitRatio),
  '--cmg-rail-rows': String(rowsPerPage.value),
  '--cmg-rail-section-count': String(sectionsOnPage.value),
}));

function pageOfRow(rowIndex: number): number {
  return Math.floor(rowIndex / rowsPerPage.value);
}

function pageOfItem(uuid: string): number {
  const rowIndex = rows.value.findIndex((row) =>
    row.entries.some(({ item }) => item.uuid === uuid)
  );
  return rowIndex === -1 ? -1 : pageOfRow(rowIndex);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function sectionLabelKey(row: GalleryRow): string | null {
  if (!(props.sections && row.sectionStart) || row.kind === 'mixed') {
    return null;
  }
  return SECTION_LABEL_KEYS[row.kind];
}

function canScroll(): boolean {
  return pageCount.value > 1;
}

// null: the item is not in these rows.
function isItemVisible(uuid: string): boolean | null {
  const target = pageOfItem(uuid);
  return target === -1 ? null : target === page.value;
}

function scrollToItem(uuid: string): boolean {
  const target = pageOfItem(uuid);
  if (target === -1) {
    return false;
  }
  page.value = target;
  return true;
}

defineExpose({ canScroll, isItemVisible, scrollToItem });
</script>

<template>
  <div
    class="ClassicMixedPortraitGallery__rows"
    :class="rootClasses"
    :style="rootStyle"
    :data-page="page"
  >
    <div class="ClassicMixedPortraitGallery__rail-frame">
      <div
        class="ClassicMixedPortraitGallery__rail"
        data-testid="classic-mixed-gallery-rail"
      >
        <div
          v-for="(row, rowIndex) in rows"
          v-show="pageOfRow(rowIndex) === page"
          :key="rowIndex"
          class="ClassicMixedPortraitGallery__row"
          :class="{
            'ClassicMixedPortraitGallery__row--section-start':
              sections && row.sectionStart && rowIndex % rowsPerPage !== 0,
          }"
          :style="{ '--cmg-row-fill': String(row.fill) }"
          :data-gallery-section="row.kind"
        >
          <div class="ClassicMixedPortraitGallery__row-tiles">
            <span
              v-if="sectionLabelKey(row)"
              class="ClassicMixedPortraitGallery__section-label"
              data-testid="classic-mixed-gallery-section-label"
            >
              {{ $t(sectionLabelKey(row) ?? '') }}
            </span>
            <ClassicPortraitGalleryTile
              v-for="entry in row.entries"
              :key="tileKey(entry)"
              v-bind="tileBindings(entry)"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="ClassicMixedPortraitGallery__pager">
      <button
        class="ClassicMixedPortraitGallery__control ClassicMixedPortraitGallery__arrow ClassicMixedPortraitGallery__arrow--prev"
        type="button"
        data-testid="classic-mixed-gallery-prev"
        :aria-label="$t('Portraits.galleryScrollBack')"
        :disabled="page <= 0"
        @click="page -= 1"
      />
      <div
        v-if="pageCount > 1"
        class="ClassicMixedPortraitGallery__dots"
        data-testid="classic-mixed-gallery-dots"
      >
        <span
          v-for="index in pageCount"
          :key="index"
          class="ClassicMixedPortraitGallery__dot"
          :class="{
            'ClassicMixedPortraitGallery__dot--active': index - 1 === page,
          }"
          :data-testid="`classic-mixed-gallery-dot-${index - 1}`"
        />
      </div>
      <button
        v-if="onLastPage && showGenerateMore"
        class="ClassicMixedPortraitGallery__control ClassicMixedPortraitGallery__more"
        type="button"
        data-testid="gallery-generate-more"
        :disabled="generateMoreDisabled"
        :aria-label="$t('PortraitsGroup.buttonMore')"
        @click="emit('generateMore')"
      >
        +
      </button>
      <button
        v-else
        class="ClassicMixedPortraitGallery__control ClassicMixedPortraitGallery__arrow ClassicMixedPortraitGallery__arrow--next"
        type="button"
        data-testid="classic-mixed-gallery-next"
        :aria-label="$t('Portraits.galleryScrollForward')"
        :disabled="onLastPage"
        @click="page += 1"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use './classic-gallery-control';

/* Rows plus two controls: a column at the rows' right edge, a row under
   them, or one on each side hanging into the screen margins like the old
   galleries' arrows (always so in portrait). The forward control is the
   next arrow until the last page, where it becomes "+". */
.ClassicMixedPortraitGallery__rows {
  --cmg-rail-arrow-size: max(var(--cmg-touch), 4rem);

  display: grid;
  grid-template-areas: 'rail pager';
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: minmax(0, 1fr);
  gap: var(--cmg-rail-gap) 1rem;
  min-width: 0;
  min-height: 0;
}

/* The frame must stay stretched: as a size container it has no height of
   its own, so centering it would collapse the rows to nothing. */
.ClassicMixedPortraitGallery__pager {
  display: flex;
  flex-direction: column;
  grid-area: pager;
  gap: 1rem;
  align-self: center;
}

.ClassicMixedPortraitGallery__dots {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
}

.ClassicMixedPortraitGallery__dot {
  width: 0.9rem;
  height: 0.9rem;
  border-radius: 50%;
  background: rgba(var(--Kiosk--font_color-rgb), 0.35);
}

.ClassicMixedPortraitGallery__dot--active {
  background: rgba(var(--Kiosk--font_color-rgb), 0.9);
}

.ClassicMixedPortraitGallery__arrow--prev::after {
  transform: rotate(-90deg);
}

.ClassicMixedPortraitGallery__arrow--next::after {
  transform: rotate(90deg);
}

.ClassicMixedPortraitGallery__rows--controls-bottom {
  grid-template-areas:
    'rail'
    'pager';
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr) auto;
}

.ClassicMixedPortraitGallery__rows--controls-bottom .ClassicMixedPortraitGallery__pager {
  flex-direction: row;
  justify-self: center;
}

.ClassicMixedPortraitGallery__rows--controls-bottom .ClassicMixedPortraitGallery__dots {
  flex-direction: row;
}

.ClassicMixedPortraitGallery__rows--controls-bottom .ClassicMixedPortraitGallery__arrow--prev::after {
  transform: rotate(180deg);
}

.ClassicMixedPortraitGallery__rows--controls-bottom .ClassicMixedPortraitGallery__arrow--next::after {
  transform: none;
}

.ClassicMixedPortraitGallery__rows--portrait {
  --cmg-rail-arrow-size: max(var(--cmg-touch), 3rem);
}

/* Only the controls are centered: the frame must stay stretched (see the
   pager note above). The dots take a row of their own under the rail. */
.ClassicMixedPortraitGallery__rows--controls-beside {
  grid-template-areas:
    'prev rail next'
    '. dots .';
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-rows: minmax(0, 1fr) auto;
  margin-inline: calc(-1 * var(--cmg-rail-arrow-outset, 4rem));
}

.ClassicMixedPortraitGallery__rows--controls-beside .ClassicMixedPortraitGallery__control {
  align-self: center;
}

.ClassicMixedPortraitGallery__rows--controls-beside .ClassicMixedPortraitGallery__pager {
  display: contents;
}

.ClassicMixedPortraitGallery__rows--controls-beside .ClassicMixedPortraitGallery__dots {
  grid-area: dots;
  flex-direction: row;
  justify-self: center;
  padding-top: 1rem;
}

.ClassicMixedPortraitGallery__rows--controls-beside .ClassicMixedPortraitGallery__arrow--prev {
  grid-area: prev;
}

.ClassicMixedPortraitGallery__rows--controls-beside .ClassicMixedPortraitGallery__arrow--prev::after {
  transform: rotate(180deg);
}

.ClassicMixedPortraitGallery__rows--controls-beside .ClassicMixedPortraitGallery__arrow--next,
.ClassicMixedPortraitGallery__rows--controls-beside .ClassicMixedPortraitGallery__more {
  grid-area: next;
}

.ClassicMixedPortraitGallery__rows--controls-beside .ClassicMixedPortraitGallery__arrow--next::after {
  transform: none;
}

/* One page keeps the back arrow's room so the rows do not jump when a page
   appears after "+". */
.ClassicMixedPortraitGallery__rows--single-page
  .ClassicMixedPortraitGallery__arrow--prev {
  visibility: hidden;
}

/* The frame is the box the rows measure against: a size container, so its
   height comes from the layout around it (the body, or the dock's share of
   it), never from the rows. The unit height is that of a vertical portrait
   one of --cmg-rail-columns across the frame, shrunk until the page's rows
   fit the frame's height; a row is the unit times its fill. */
.ClassicMixedPortraitGallery__rail-frame {
  display: flex;
  flex-direction: column;
  justify-content: center;
  grid-area: rail;
  container-type: size;
  min-width: 0;
  min-height: 0;
}

.ClassicMixedPortraitGallery__rail {
  --cmg-rail-fit-unit-height: calc(
    (
        100cqh - (var(--cmg-rail-rows) - 1) * var(--cmg-rail-gap) -
          var(--cmg-rail-section-count, 0) * var(--cmg-rail-divider, 0px)
      ) /
      var(--cmg-rail-fill-sum, var(--cmg-rail-rows))
  );
}

.ClassicMixedPortraitGallery__row {
  --cmg-rail-row-height: calc(
    var(--cmg-rail-unit-height) * var(--cmg-row-fill, 1)
  );
}

.ClassicMixedPortraitGallery__rows--divided {
  --cmg-rail-divider: calc(var(--cmg-rail-gap) + 1px);
}

.ClassicMixedPortraitGallery__rail {
  --cmg-rail-unit-height: min(
    calc(
      (100cqw - (var(--cmg-rail-columns) - 1) * var(--cmg-rail-gap)) /
        var(--cmg-rail-columns) / var(--cmg-rail-portrait-ratio)
    ),
    var(--cmg-rail-fit-unit-height, 99999px)
  );
  --cmg-rail-row-height: var(--cmg-rail-unit-height);

  display: flex;
  flex-direction: column;
  gap: var(--cmg-rail-gap);
}

/* Rows of equal height: tiles are --cmg-rail-row-height tall and as wide as
   their ratio needs (see the tile); the script packs them into rows. Rows
   are centered: when the tallest row is capped by the frame's height (a
   group portrait on its own row cannot be as wide as the frame without
   being taller than it), the block sits in the middle instead of hugging
   the left edge. */
.ClassicMixedPortraitGallery__row {
  --cmg-tile-max-height: var(--cmg-rail-row-height);

  display: flex;
  justify-content: center;
}

/* The tiles of a row, shrink-wrapped, so a caption can sit on the first
   one; the divider stays the full width of the row. */
.ClassicMixedPortraitGallery__row-tiles {
  position: relative;
  display: flex;
  gap: var(--cmg-rail-gap);
  max-width: 100%;
}

/* The gap alone separates the sections. Their captions already name them,
   thus a rule between them only draws over the kiosk art. */
.ClassicMixedPortraitGallery__row--section-start {
  margin-top: calc(var(--cmg-rail-gap) / 2);
  padding-top: calc(var(--cmg-rail-gap) / 2);
}

/* A section's caption sits in the corner of its first tile; that tile's
   selected mark moves to the bottom corner so the two never collide (the
   compact counter is bottom-centre and stays clear of it). */
.ClassicMixedPortraitGallery__section-label
  + :deep(
    .ClassicMixedPortraitGallery__thumbnail
      .ClassicMixedPortraitGallery__thumbnail-mark
  ) {
  top: auto;
  bottom: 0.45rem;
}

.ClassicMixedPortraitGallery__section-label {
  position: absolute;
  top: 0.55rem;
  left: 0.55rem;
  z-index: 3;
  padding: 0.3rem 0.7rem;
  font-size: 0.85rem;
  letter-spacing: 0.08em;
  color: var(--Kiosk--tint-inverse-color);
  text-transform: uppercase;
  pointer-events: none;
  background: rgba(var(--Kiosk--tint-color-rgb), 0.72);
  border-radius: 99rem;
}

</style>
